import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User } from "../models/userModel";
import { clearStoredTokens, decodeJWT, getTokens, sendPasswordResetEmail, signIn, signOut, storeTokens } from "../services/auth";
import userProfileService from "../services/userProfileService";
import corporateAccountService from "../services/corporateAccountService";
import subscriptionService from "../services/subscriptionService";
import { CorporateAccount } from "../models/corporateAccountModel";
import { LocationHostUser } from "../models/locationHostUserModel";

// UserContext type
interface UserContextType {
  user: User | null;
  isLoggedIn: boolean;
  login: (userLoginData: UserLoginData) => Promise<any>;
  logout: () => void;
    email: string;
    password: string;
    setEmail: (e: string) => void;
    setPassword: (e: string) => void;
    userCorporateAccount?: CorporateAccount | null;
    setUserCorporateAccount: (ca: CorporateAccount) => void;
    userCorporateAccountUserHosts?: LocationHostUser[] | null;
    setUserInfo: (firstName: string, lastName: string, phone: string) => void;
    authLoading: boolean;
    forgotPassword: (email: string) => void;
}

interface Session {
    access_token: string;
    token_type: string;
    expires_in: number;
    expires_at: number | undefined;
    refresh_token: string;
}

interface UserLoginData {
    email?: string,
    password?: string,
    validToken?: string,
    refreshToken?: string,
}
const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error("useUser must be used within a UserProvider");
  return ctx;
};

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
    const [user, setUser] = useState<User | null>(null);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');    
    const [userCorporateAccount, setUserCorporateAccount] = useState<CorporateAccount | null>(null);
    const [userCorporateAccountUserHosts, setUserCorporateAccountUserHosts] = useState<LocationHostUser[] | null>(null);
    const [authLoading, setAuthLoading] = useState(true);

    const setUserInfo = (firstName: string, lastName: string, phone: string) => {
    const updatedUser: any = {
         ...user,
         firstName,
         lastName,
         phone,
      };
      setUser(updatedUser);
    };

        const forgotPassword = async(email: string): Promise<boolean> => {
        try{
            const emailSent = await sendPasswordResetEmail(email);
            return emailSent;
        }catch(error){
            throw error;
        }
    };
   

    const login = async (userLoginData: UserLoginData) => {
        try{
            let sub;
            let email;
            //if the user has a valid token, use that instead of calling login
            if(userLoginData.validToken){
                const tokenUser: any = decodeJWT(userLoginData.validToken);
                if(tokenUser && tokenUser.sub && tokenUser.aud == 'authenticated'){                    
                    sub = tokenUser.sub;    
                    email = tokenUser.email;
                }else{
                    //clear these tokens so next time it forces new login
                    await clearStoredTokens();
                    return { error: 'There was a problem logging in. Please try again.'};
                }
            }else{
                const {data, error} = await signIn(userLoginData.email, userLoginData.password);
                if(data.user && data.user.aud == 'authenticated'){
                    //set userid
                    sub = data.user.user_metadata.sub;
                    email = data.user.user_metadata.email;
                    //store new tokens
                    await storeTokens(data.session.access_token, data.session.refresh_token);
                }else{
                    console.debug('login error:', error);
                    if(error && error.message){
                        return { error: error.message };
                    }
                    return { error };
                }
            }                
            //get user profile
            const { data: userProfile, error: profileError } = await userProfileService.fetchProfile(sub);

            //if the user has been deleted, return no login allowed
            if(userProfile && userProfile[0]){
                const checkingProfile = userProfile[0];
                if(checkingProfile.isDeleted == true){
                    return { error: 'Account does not exist'};
                }
            }
            //const { data: strikeData, error: strikeError } = await strikeService.fetchUserStrikes(sub);
            const { data: subscriptionData, error: subscriptionError } = await subscriptionService.fetchActiveSubscription(sub);
            //const { data: suspensionData, error: suspensionError } = await locationSuspendService.fetchUserLocationSuspendsForUser(sub);
            const foundSubscription = subscriptionData && subscriptionData[0] ? subscriptionData[0] : null;

            let subIsActive = foundSubscription && foundSubscription.isActive;
            if(foundSubscription && !foundSubscription.isActive){
                const checkDate = new Date().toISOString();
                subIsActive = foundSubscription.cancelDate > checkDate;
            } 
            //set
            //setUserStrikes(strikeData);
            //setUserSuspensions(suspensionData);

            if(userProfile && userProfile[0]){
                const foundProfile = userProfile[0];
                let user = {
                    id: sub,// data.user.user_metadata.sub,
                    email: email,// data.user.user_metadata.email,
                    firstName: foundProfile.firstName,
                    lastName: foundProfile.lastName,
                    phone: foundProfile.phone,
                    profilePic: foundProfile.profilePic,
                    address: foundProfile.address,
                    city: foundProfile.city,
                    state: foundProfile.state,
                    zip: foundProfile.zip,
                    isBanned: foundProfile.isBanned,
                    hasStrike: false,
                    numberOfStrikes: 0,
                    isPremiumUser: subIsActive, // foundProfile.isPremiumUser,
                    subscriptionId: foundSubscription ? foundSubscription.subscriptionId : -1,
                    subscriptionCancelDate: foundSubscription ? foundSubscription.cancelDate : null,
                    numberofSuspensions: 0,
                    isMaintenanceWorker: false,
                    isTester: foundProfile.isTester,
                    isCorporateAccountOwner: foundProfile.isCorporateAccountOwner
                };
                //if we need to, go get the corporate account information
                if(foundProfile.isCorporateAccountOwner){
                    var caInfo = await corporateAccountService.getCorporateAccountUser(sub);
                    setUserCorporateAccount(caInfo);
                }else{

                    //see if they are an admin of a corporate account...
                    var caadminInfo = await corporateAccountService.getCorporateAccountAdminByEmailOfUser(email);
                    //if there is a corprate account, get and set that information
                    if(caadminInfo){
                        var caInfo = await corporateAccountService.getCorporateAccount(caadminInfo.corporateAccountId);
                        //user.isCorporateAccountAdmin = true;
                        setUserCorporateAccount(caInfo);
                    }else{
                        //console.log('no corporate account access for user');
                        //they are not an admin - do not allow login
                        return { error: 'No corporate account access. If you are a location host admin, please use the app to manage your location.'};
                        // //see if their email has an records in the LocationHostUser table - if it does, they have been assigned as a host there
                        // var cauInfo = await corporateAccountService.getCorporateAccountLocationHostUsers_PerEmail(email);
                        // setUserCorporateAccountUserHosts(cauInfo);
                    }
                }
                //set user properties
                setUser(user);
                //set user is logged in
                setIsLoggedIn(true);
                return { error: null};
            }else{
                //problem getting profile
                return { error: profileError};
            }          
        }catch(error){
            throw error;
        }
    };

    const logout = async () => {
        try{
            const loggedOut = await signOut();
            setUser(null);
            setEmail('');
            setPassword('');
            setIsLoggedIn(false);
            return loggedOut;
        }catch(error){
            throw error;
        }
    };

    useEffect(() => {
        const restoreSession = async () => {
            try {
            const tokens = await getTokens();
            if (tokens?.accessToken && tokens.refreshToken) {
                const result = await login({
                validToken: tokens.accessToken,
                refreshToken: tokens.refreshToken,
                });
                //console.log("Login result:", result);
            }
            } catch (err) {
            //console.error("Session restore failed", err);
            await clearStoredTokens();
            } finally {
            setAuthLoading(false);
            }
        };

        restoreSession();
        }, []);


  return (
    <UserContext.Provider value={{ user, 
                                    isLoggedIn, 
                                    login, logout, 
                                    email, setEmail,
                                    password, setPassword, 
                                    userCorporateAccount, setUserCorporateAccount, 
                                    userCorporateAccountUserHosts, 
                                    setUserInfo,
                                    forgotPassword, 
                                    authLoading }}>
      {children}
    </UserContext.Provider>
  );
};


